import type { CodeSkillSelection } from '@/lib/code-sessions/skills';
import type { CodeSessionAgentEvent, CodeSessionEvent } from '@/lib/code-sessions/types';
import type { QueryClient } from '@tanstack/react-query';

import type { SessionConnection, SessionConversation } from './runtime/types';
import type { LocalTurnDispatch } from './turn-activity';
import type {
  CodeFormAnswer,
  CodeFormSubmission,
  CodePromptSubmission,
  CodeSessionTurn,
  PendingPrompt,
  SessionResponse,
} from './types';

import { turnPromptAttachments } from './attachments';
import { codeEnvironmentKeys } from './environment';
import { codeEnvironmentCheckKeys } from './environment-check';
import { advanceCursor, appendEvents, mergeBySeq } from './event-log';
import { codeSessionKeys, mergeSavedTurn } from './queries';
import { applyRewindEvent, commitRewindForNewTurn } from './rewind-cache';
import { isRuntimeReady } from './runtime/capabilities';
import { codeRuntimeOf, runtimeFor } from './runtime/runtime-for';
import { isLocalSessionId } from './runtime/session-id';
import { listsFor } from './runtime/session-lists';
import { StreamDeltaCoalescer } from './stream-delta-coalescer';
import {
  deriveTurnActivity,
  listedStartsWork,
  listedWork,
  movesTurnActivity,
} from './turn-activity';
import { isPersonTurn } from './turn-prompt';
import { refreshSessionGit, refreshSessionWorkspace } from './workspace-refresh';

import { isQuestionFormEvent } from '@/lib/code-sessions/awaiting-input-events';
import { isTerminalTurnStatus, isUndeliveredTurnStatus } from '@/lib/code-sessions/turn-utils';

type CodeSessionRewindEvent = Extract<CodeSessionEvent, { kind: 'rewind' }>;

export type SessionTransportState = {
  /** Ordered, deduped events (cached events + live stream). */
  events: CodeSessionEvent[];
  /** The events that move turn activity; see `movesTurnActivity`. */
  turnActivityEvents: CodeSessionEvent[];
  /** Agent events with each stream's consecutive token deltas joined; see `StreamDeltaCoalescer`. */
  streamEvents: CodeSessionAgentEvent[];
  /** Last contiguous Cubic sequence, kept separate from OpenCode transcript cursors. */
  cursor: number;
  /** Local content stays available until matching saved content is in query.data. */
  pendingPrompts: (PendingPrompt & { skills?: CodeSkillSelection[] })[];
  /** Retain local ordering after server content replaces each optimistic bubble. */
  promptCreatedAt: Record<string, number>;
  localDispatches: Record<string, LocalTurnDispatch>;
  cancellingTurnIds: string[];
  /**
   * Steers this tab asked to cut in, by a composer steer sent while the agent
   * works or by Send now, until the request fails or the agent takes them.
   */
  cuttingInTurnIds: string[];
  interrupt: { requestId: string; turnIds: string[]; afterSeq: number } | null;
  answers: Record<string, CodeFormSubmission>;
  /** The live feed as the mounted sync reports it, before any grace; `live` with no sync. */
  connection: SessionConnection;
};

const emptyState = (): SessionTransportState => ({
  events: [],
  turnActivityEvents: [],
  streamEvents: [],
  cursor: 0,
  pendingPrompts: [],
  promptCreatedAt: {},
  localDispatches: {},
  cancellingTurnIds: [],
  cuttingInTurnIds: [],
  interrupt: null,
  answers: {},
  connection: 'live',
});

/** Events after which open workspace panels must refresh their files. */
function touchesWorkspace(event: CodeSessionEvent): boolean {
  return (
    (event.kind === 'turn' && isTerminalTurnStatus(event.status)) ||
    (event.kind === 'sandbox_status' && event.status === 'ready') ||
    (event.kind === 'session_status' && Boolean(event.transcriptReady)) ||
    // Staging and clearing a rewind restore files on disk.
    event.kind === 'rewind'
  );
}

/**
 * Git reads need the saved idle status, which lands after a turn's terminal
 * event. Clearing a check-in repeats it without changing files.
 */
function settlesIdle(event: CodeSessionEvent): boolean {
  return event.kind === 'session_status' && event.status === 'idle';
}

/** One controller per QueryClient/session, retained for exactly the lifetime of its detail cache. */
export class CodeSessionController {
  private state = emptyState();
  private listeners = new Set<() => void>();
  private seeded = false;
  /** The live stream subscribed before the first snapshot, so seeding replays its gap. */
  private recoverOnSeed = false;
  private snapshot: SessionResponse | undefined;
  private recovery: Promise<void> | undefined;
  private recoveryAbort: AbortController | undefined;
  /** The read `saveTurn` started for someone its people do not name yet, while it runs. */
  private peopleRead: Promise<void> | undefined;
  private disposed = false;
  private lastSubmittedAt = 0;
  private lastGapRecoveryAt = -Infinity;
  private lastRewindSeq = 0;
  private connectionRetry: (() => void) | null = null;
  private streamCoalescer = new StreamDeltaCoalescer();
  private promptRequests = new Map<
    string,
    {
      settled: Promise<Error | null>;
      finish: (error: Error | null) => void;
    }
  >();

  constructor(
    private client: QueryClient,
    readonly sessionId: string,
    /**
     * Where the conversation is read from and sent to: the session's runtime's
     * (`runtimeFor`), unless the creator says otherwise.
     */
    readonly conversation: SessionConversation = runtimeFor(sessionId).conversation,
  ) {}

  /** The session runs on the person's own computer: cubic keeps no row, list or environment for it. */
  private get local() {
    return isLocalSessionId(this.sessionId);
  }

  private get queue() {
    return codeRuntimeOf(this.sessionId).queue;
  }

  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private publish(state: SessionTransportState) {
    if (this.disposed) {
      return;
    }
    this.state = state;
    this.listeners.forEach((listener) => listener());
  }

  seed(snapshot: SessionResponse, updateSummary = true) {
    if (snapshot === this.snapshot || this.disposed) {
      return;
    }
    const workspaceBecameReady =
      this.snapshot !== undefined && !isRuntimeReady(this.snapshot) && isRuntimeReady(snapshot);
    this.snapshot = snapshot;
    if (workspaceBecameReady) {
      void refreshSessionWorkspace(this.client, this.sessionId);
    }
    // The turn row owns authored text and skills; runtime messages contain only
    // the combined prompt and cannot replace that submission metadata.
    const confirmedIds = new Set(
      snapshot.session.turns
        .filter(
          (turn) =>
            turn.prompt.length > 0 || turnPromptAttachments(this.sessionId, turn).length > 0,
        )
        .map((turn) => turn.id),
    );
    // Like T3/OpenCode, a status acknowledgment is not replacement content.
    // Snapshot cursor only describes its event log; it does not version the DB row or transcript.
    const firstSeq = snapshot.events[0]?.seq;
    let held: CodeSessionEvent[] = [];
    if (!this.seeded) {
      // Live events that arrived while the first snapshot loaded had no row to
      // refetch; those its log does not hold can be newer than its row.
      held = this.state.events.filter((event) => event.seq > (snapshot.cursor ?? 0));
      // Only the snapshot places the cursor: starting it at a live event that
      // arrived first would skip the events missing between the two.
      this.state = {
        ...this.state,
        cursor: firstSeq === undefined ? (snapshot.cursor ?? 0) : Math.max(0, firstSeq - 1),
      };
      this.seeded = true;
    }
    // A saved turn past admission has reached the agent; one not saved yet
    // may still be in flight.
    const turnStatus = new Map(snapshot.session.turns.map((turn) => [turn.id, turn.status]));
    this.mergeEvents(snapshot.events, {
      pendingPrompts: this.state.pendingPrompts.filter(
        (prompt) => !confirmedIds.has(prompt.clientId),
      ),
      cuttingInTurnIds: this.state.cuttingInTurnIds.filter((id) => {
        const status = turnStatus.get(id);
        return status === undefined || isUndeliveredTurnStatus(status);
      }),
      // On the person's own computer a read lists every prompt the server holds, so a dispatch
      // it lacks went with its prompt, delivered or removed while nothing reached the page, and
      // would otherwise stay a queued row with no text. One whose send is still on its way
      // keeps its row. The cloud saves every turn, so its dispatches find theirs.
      localDispatches:
        this.queue === 'inbox'
          ? Object.fromEntries(
              Object.entries(this.state.localDispatches).filter(
                ([id]) => turnStatus.has(id) || this.promptRequests.has(id),
              ),
            )
          : this.state.localDispatches,
    });
    this.invalidateFor(held);
    if (this.recoverOnSeed) {
      this.recoverOnSeed = false;
      void this.recover();
    }
    if (updateSummary) {
      void listsFor(this.sessionId).update(this.client, snapshot.session);
    }
  }

  /** Merge events into the log; returns the ones it had not seen. */
  private mergeEvents(
    incoming: readonly CodeSessionEvent[],
    patch: Partial<SessionTransportState> = {},
  ): CodeSessionEvent[] {
    const { events, added, rewritten } = mergeBySeq(this.state.events, incoming);
    if (added.some(touchesWorkspace)) {
      // The embedded runtime has no filesystem watchers. Terminal turns and
      // resumed runtimes are the authoritative points to refresh open panels.
      void refreshSessionWorkspace(this.client, this.sessionId);
    } else if (added.some(settlesIdle)) {
      void refreshSessionGit(this.client, this.sessionId);
    }
    // Views over the log fold only the new events unless the log was rewritten.
    if (rewritten) {
      this.streamCoalescer = new StreamDeltaCoalescer();
    }
    const unfolded = rewritten ? events : added;
    const moved = unfolded.filter(movesTurnActivity);
    const turnActivityEvents = rewritten
      ? moved
      : appendEvents(this.state.turnActivityEvents, moved);
    const streamEvents = this.streamCoalescer.append(unfolded);
    const cursor = advanceCursor(events, this.state.cursor);
    let interrupt = this.state.interrupt;
    if (interrupt) {
      const { afterSeq } = interrupt;
      const terminal = new Set(
        (this.snapshot?.session.turns ?? [])
          .filter((turn) => isTerminalTurnStatus(turn.status))
          .map((turn) => turn.id),
      );
      for (const event of events) {
        if (event.kind === 'turn' && isTerminalTurnStatus(event.status)) {
          terminal.add(event.turnId);
        }
      }
      const stopped = events.some(
        (event) =>
          event.seq > afterSeq &&
          event.kind === 'session_status' &&
          ['idle', 'stopped', 'failed', 'archived'].includes(event.status),
      );
      if (
        stopped ||
        (interrupt.turnIds.length > 0 && interrupt.turnIds.every((id) => terminal.has(id)))
      ) {
        interrupt = null;
      }
    }
    this.publish({
      ...this.state,
      ...patch,
      events,
      turnActivityEvents,
      streamEvents,
      cursor,
      interrupt,
    });
    return added;
  }

  /**
   * Apply newly merged rewind events to the cached row. Snapshots already carry
   * the rows their events produced; only unseen events change the row.
   */
  private applyRewinds(added: readonly CodeSessionEvent[]) {
    // A replayed older event must not undo a newer one that already arrived live.
    const ordered = added
      .filter(
        (event): event is CodeSessionRewindEvent =>
          event.kind === 'rewind' && event.seq > this.lastRewindSeq,
      )
      .sort((a, b) => a.seq - b.seq);
    const last = ordered.at(-1);
    if (this.disposed || last === undefined) {
      return;
    }
    this.lastRewindSeq = last.seq;
    this.client.setQueryData<SessionResponse>(codeSessionKeys.detail(this.sessionId), (data) =>
      data ? { ...data, session: ordered.reduce(applyRewindEvent, data.session) } : data,
    );
    // Events carry no row version. Cancel reads that predate the rewind and
    // refetch the canonical row.
    void this.refresh({ cancelRefetch: true });
  }

  /**
   * A turn that ends done can save the repository's first environment from
   * its suggestion (`first-environment.ts`), and start its check, before it
   * relays the end: the suggestion's card then reads both again.
   */
  private invalidateEnvironment({
    installationId,
    repositoryId,
  }: {
    installationId: number;
    repositoryId: number;
  }) {
    if (this.local) {
      return;
    }
    void this.client.invalidateQueries({
      queryKey: codeEnvironmentKeys.detail({ installationId, repositoryId }),
    });
    void this.client.invalidateQueries({
      queryKey: codeEnvironmentCheckKeys.session(this.sessionId),
    });
  }

  private invalidateSideAgents(events: readonly CodeSessionEvent[]) {
    // A side agent started, by a person in another tab or by the main agent.
    if (events.some((event) => event.kind === 'side_agents')) {
      void this.client.invalidateQueries({ queryKey: codeSessionKeys.sideAgents(this.sessionId) });
    }
  }

  receive(events: readonly CodeSessionEvent[]) {
    if (this.disposed || events.length === 0) {
      return;
    }
    this.applyRewinds(this.mergeEvents(events));
    if (this.seeded && (this.state.events.at(-1)?.seq ?? 0) > this.state.cursor) {
      void this.recover(false);
    }
    this.invalidateFor(events);
  }

  /**
   * The live stream (re)subscribed: replay the events it missed. Before the
   * first snapshot there is no cursor to replay from, so seeding replays.
   */
  subscribed() {
    if (this.seeded) {
      void this.recover();
    } else {
      this.recoverOnSeed = true;
    }
  }

  /** How the mounted sync's feed stands, and what the banner's Retry runs. */
  reportConnection(connection: SessionConnection, retry: (() => void) | null) {
    this.connectionRetry = retry;
    if (this.state.connection !== connection) {
      this.publish({ ...this.state, connection });
    }
  }

  /**
   * The banner's Retry: rejoin the channel, or start the agent on this computer again, then read
   * the session again.
   */
  retryConnection = (): void => this.connectionRetry?.();

  /** Refetch what events make stale, whether they arrived live or by replay. */
  private invalidateFor(events: readonly CodeSessionEvent[]) {
    this.invalidateSideAgents(events);
    const data = this.client.getQueryData<SessionResponse>(codeSessionKeys.detail(this.sessionId));
    if (data) {
      const statusChanged = events.some((event) => event.kind === 'session_status');
      const known = new Map(data.session.turns.map((turn) => [turn.id, turn]));
      // How turns newly ended, including ones the row does not list yet.
      const endings = events.flatMap((event) =>
        event.kind === 'turn' &&
        isTerminalTurnStatus(event.status) &&
        known.get(event.turnId)?.status !== event.status
          ? [event.status]
          : [],
      );
      // A turn the row lists that waits at another place (a move) or with
      // another text (an edit, `edited`): a read already under way may predate
      // that change. Every held follow-up announces `queued` too, so the event
      // of one the row already lists where it waits changes nothing.
      const changedWhileQueued = events.some((event) => {
        if (event.kind !== 'turn' || event.status !== 'queued') {
          return false;
        }
        const listed = known.get(event.turnId);
        return listed !== undefined && (listed.index !== event.index || event.edited === true);
      });
      // On this computer a prompt's enqueue, delivery or cancel changes OpenCode's inbox or its
      // messages, which only a read shows, and a read already under way may predate it. The
      // cloud relays a turn event for each of these, handled above.
      const inboxChanged =
        this.queue === 'inbox' &&
        events.some((event) => event.kind === 'agent' && event.type.startsWith('session.inbox.'));
      // A change to the session's rules, which set the approval mode its row shows, likewise
      // shows only in a read, whether this window or another made it. A row with no mode, as a
      // cloud session's, whose rules cubic sets for its own turns, needs no read.
      const rulesChanged =
        data.session.approvalMode !== undefined &&
        events.some((event) => event.kind === 'agent' && event.type === 'session.permissions');
      if (
        statusChanged ||
        changedWhileQueued ||
        inboxChanged ||
        rulesChanged ||
        endings.length > 0 ||
        events.some((event) => event.kind === 'turn' && !known.has(event.turnId))
      ) {
        void this.refresh({
          cancelRefetch: statusChanged || changedWhileQueued || inboxChanged || rulesChanged,
        });
      }
      // The row says done before the turn saves its first environment and relays
      // the end (`turn-end.ts`), so every done event reads them again.
      if (events.some((event) => event.kind === 'turn' && event.status === 'done')) {
        this.invalidateEnvironment(data.session);
      }
      // The server saves whether a question waits on the row before it relays these events.
      const questionChanged = events.some(
        (event) => event.kind === 'agent' && isQuestionFormEvent(event.type),
      );
      // Events have a stream sequence, not a database row version. Refetch
      // the canonical row instead of stamping an event onto an older snapshot.
      // Cancel pre-transition reads in every observing tab, not just the tab
      // that initiated Archive/Undo. Each runtime reads its lists again only
      // for the changes they show.
      listsFor(this.sessionId).invalidate(this.client, data.session, {
        status: statusChanged,
        question: questionChanged,
      });
    }
  }

  /**
   * The live stream lost events it cannot replay: forget every event this tab
   * holds and read the session again, as a reload would. The fresh read merges
   * into the cached snapshot as every read does (`mergeSessionSnapshot`), and
   * places the cursor anew, so its numbering may start over. This tab's own
   * unconfirmed prompts go with the log, as below; its answers stay.
   */
  resync() {
    if (this.disposed) {
      return;
    }
    this.recoveryAbort?.abort();
    this.seeded = false;
    this.recoverOnSeed = false;
    this.lastRewindSeq = 0;
    this.streamCoalescer = new StreamDeltaCoalescer();
    // The log goes, numbering included, and with it this tab's prompts the server never confirmed:
    // the fresh read lists every prompt the server holds, so a row it lacks would have no server
    // side for Remove or Steer to reach. A prompt whose send is still on its way stays, text and
    // all, as its dispatch does, until the send answers. The cached snapshot stays on view until
    // the fresh read lands and replaces it; a read that fails leaves the view as it is, with its
    // notice.
    this.publish({
      ...this.state,
      events: [],
      turnActivityEvents: [],
      streamEvents: [],
      cursor: 0,
      interrupt: null,
      pendingPrompts: this.state.pendingPrompts.filter((prompt) =>
        this.promptRequests.has(prompt.clientId),
      ),
    });
    void this.client.invalidateQueries({
      queryKey: codeSessionKeys.detail(this.sessionId),
      exact: true,
    });
  }

  refresh = ({ cancelRefetch = false }: { cancelRefetch?: boolean } = {}) =>
    this.disposed
      ? Promise.resolve()
      : this.client.invalidateQueries(
          { queryKey: codeSessionKeys.detail(this.sessionId), exact: true },
          { cancelRefetch },
        );

  recover = (force = true): Promise<void> => {
    if (this.disposed) {
      return Promise.resolve();
    }
    if (this.recovery) {
      return this.recovery;
    }
    // A temporarily missing append or expired log must not cause a request for every token.
    if (!force && Date.now() - this.lastGapRecoveryAt < 1_000) {
      return Promise.resolve();
    }
    this.lastGapRecoveryAt = Date.now();
    this.recoveryAbort = new AbortController();
    this.recovery = this.conversation
      .replay(this.state.cursor, { signal: this.recoveryAbort.signal })
      .then(({ events }) => {
        if (!this.disposed) {
          const added = this.mergeEvents(events);
          this.applyRewinds(added);
          // The replay repeats live events the log holds, which had their refetches.
          this.invalidateFor(added);
          if (force) {
            // A reconnect can miss filesystem changes even when no new events
            // remain in the replay buffer (for example, after a Git action).
            void refreshSessionWorkspace(this.client, this.sessionId);
          }
        }
      })
      .catch(() => {
        // Keep the missing sequence pending; a failed replay must not advance the cursor.
        if (!this.disposed) {
          void this.refresh();
        }
      })
      .finally(() => {
        this.recovery = undefined;
      });
    return this.recovery;
  };

  beginPrompt(clientId: string, submission: CodePromptSubmission) {
    const { text, delivery, skills, authoredText, attachments } = submission;
    let finish!: (error: Error | null) => void;
    const settled = new Promise<Error | null>((resolve) => {
      finish = resolve;
    });
    this.promptRequests.set(clientId, { settled, finish });
    const createdAt = Math.max(Date.now(), this.lastSubmittedAt + 1);
    this.lastSubmittedAt = createdAt;
    // A steer waits for the agent's next step like a queued prompt waits for
    // its turn. One sent while the agent works cuts in within its request.
    const activity = this.activeTurnStatuses();
    const queued = activity.size > 0;
    const cutsIn = delivery === 'steer' && (activity.has('running') || activity.has('waiting'));
    this.publish({
      ...this.state,
      pendingPrompts: [
        ...this.state.pendingPrompts,
        {
          clientId,
          turnId: clientId,
          text,
          authoredText,
          delivery,
          createdAt,
          attachments,
          ...(skills.length ? { skills } : {}),
        },
      ],
      promptCreatedAt: { ...this.state.promptCreatedAt, [clientId]: createdAt },
      localDispatches: {
        ...this.state.localDispatches,
        [clientId]: { createdAt, queued, delivery },
      },
      cuttingInTurnIds: cutsIn
        ? [...this.state.cuttingInTurnIds, clientId]
        : this.state.cuttingInTurnIds,
    });
  }

  /**
   * Pending content can outlive a completed turn while its POST is in flight.
   * Only active work should queue a new send; retained text is not a queue lock.
   */
  private activeTurnStatuses() {
    const listed = listedWork(
      listsFor(this.sessionId).find(this.client, this.sessionId),
      this.snapshot?.session,
    );
    const activity = deriveTurnActivity(
      (this.snapshot?.session.turns ?? []).map((turn) => ({
        ...turn,
        createdAt: Date.parse(turn.createdAt),
        startedAt: turn.startedAt === null ? undefined : Date.parse(turn.startedAt),
        endedAt: turn.endedAt === null ? null : Date.parse(turn.endedAt),
      })),
      this.state.turnActivityEvents,
      this.state.localDispatches,
      { listed },
    );
    return new Set([
      ...activity
        .map((turn) => turn.status)
        .filter((status) => ['starting', 'queued', 'running', 'waiting'].includes(status)),
      // A newer list row says the session works, which its cached detail missed: a prompt queues
      // behind that work, and a steer cuts in.
      ...(listedStartsWork(listed, activity) ? (['running'] as const) : []),
    ]);
  }

  rejectPrompt(clientId: string, uncertain: boolean) {
    const localDispatches = { ...this.state.localDispatches };
    delete localDispatches[clientId];
    this.publish({
      ...this.state,
      localDispatches,
      // A send that may have failed cannot claim to be cutting in.
      cuttingInTurnIds: this.state.cuttingInTurnIds.filter((id) => id !== clientId),
      pendingPrompts: uncertain
        ? this.state.pendingPrompts
        : this.state.pendingPrompts.filter((prompt) => prompt.clientId !== clientId),
    });
  }

  finishPrompt(clientId: string, error: Error | null) {
    this.promptRequests.get(clientId)?.finish(error);
    this.promptRequests.delete(clientId);
  }

  async waitForPrompt(turnId: string) {
    // Steer/Remove may be clicked on an optimistic queue row before its POST
    // creates the DB turn. Keep the UI optimistic, but serialize its HTTP action.
    const request = this.promptRequests.get(turnId);
    if (request) {
      const error = await request.settled;
      if (error) {
        throw error;
      }
    }
  }

  setCancelling(turnId: string, cancelling: boolean) {
    this.publish({
      ...this.state,
      cancellingTurnIds: cancelling
        ? [...this.state.cancellingTurnIds.filter((id) => id !== turnId), turnId]
        : this.state.cancellingTurnIds.filter((id) => id !== turnId),
    });
  }

  /** Mark a steer as cutting in, or clear it when its request did not interrupt. */
  setCuttingIn(turnId: string, cuttingIn: boolean) {
    if (this.state.cuttingInTurnIds.includes(turnId) === cuttingIn) {
      return;
    }
    const others = this.state.cuttingInTurnIds.filter((id) => id !== turnId);
    this.publish({ ...this.state, cuttingInTurnIds: cuttingIn ? [...others, turnId] : others });
  }

  beginInterrupt(turnIds: string[]) {
    if (this.state.interrupt) {
      throw new Error('An interrupt is already in progress');
    }
    const requestId = crypto.randomUUID();
    this.publish({
      ...this.state,
      interrupt: { requestId, turnIds, afterSeq: this.state.events.at(-1)?.seq ?? 0 },
    });
    return requestId;
  }

  rejectInterrupt(requestId: string) {
    if (this.state.interrupt?.requestId === requestId) {
      this.publish({ ...this.state, interrupt: null });
    }
  }

  beginAnswer(formId: string, answer: CodeFormAnswer) {
    if (
      this.state.answers[formId]?.status === 'submitting' ||
      this.state.answers[formId]?.status === 'submitted'
    ) {
      throw new Error('This answer has already been submitted');
    }
    const requestId = crypto.randomUUID();
    this.publish({
      ...this.state,
      answers: { ...this.state.answers, [formId]: { requestId, answer, status: 'submitting' } },
    });
    return requestId;
  }

  finishAnswer(formId: string, requestId: string, error?: string) {
    const submission = this.state.answers[formId];
    if (submission?.requestId !== requestId) {
      return;
    }
    this.publish({
      ...this.state,
      answers: {
        ...this.state.answers,
        [formId]: {
          ...submission,
          status: error === undefined ? 'submitted' : 'failed',
          error,
        },
      },
    });
  }

  saveTurn(turn: CodeSessionTurn) {
    if (this.disposed) {
      return;
    }
    const key = codeSessionKeys.detail(this.sessionId);
    const people = this.client.getQueryData<SessionResponse>(key)?.people;
    this.client.setQueryData<SessionResponse>(key, (data) => {
      if (!data) {
        return data;
      }
      const turns = new Map(data.session.turns.map((saved) => [saved.id, saved]));
      const created = !turns.has(turn.id);
      turns.set(turn.id, mergeSavedTurn(turns.get(turn.id), turn));
      const session = { ...data.session, turns: [...turns.values()] };
      return { ...data, session: created ? commitRewindForNewTurn(session, turn) : session };
    });
    // A person writing here for the first time joins the session's people only on its next
    // read, which Share and the prompts' labels name them from; so does everyone, when the last
    // read could not name them. A read already under way may predate the turn, so it starts
    // again; one started here already covers this tab's later messages, all its viewer's.
    if (
      !this.peopleRead &&
      isPersonTurn(turn) &&
      !people?.some((person) => person.profileId === turn.authorProfileId)
    ) {
      this.peopleRead = this.refresh({ cancelRefetch: true }).finally(() => {
        this.peopleRead = undefined;
      });
    }
  }

  dispose() {
    this.recoveryAbort?.abort();
    for (const request of this.promptRequests.values()) {
      request.finish(new Error('Session was closed before the message was confirmed'));
    }
    this.promptRequests.clear();
    this.publish(emptyState());
    this.disposed = true;
    this.listeners.clear();
  }
}

const controllers = new WeakMap<QueryClient, Map<string, CodeSessionController>>();

export function getCodeSessionController(client: QueryClient, sessionId: string) {
  let sessions = controllers.get(client);
  if (!sessions) {
    sessions = new Map();
    controllers.set(client, sessions);
    const ownedSessions = sessions;
    client.getQueryCache().subscribe((event) => {
      const [prefix, id] = event.query.queryKey;
      if (prefix !== 'code-session' || typeof id !== 'string') {
        return;
      }
      const controller = ownedSessions.get(id);
      if (event.type === 'removed') {
        controller?.dispose();
        ownedSessions.delete(id);
        codeRuntimeOf(id).forget(id);
      } else if (event.type === 'updated' && event.query.state.data) {
        controller?.seed(event.query.state.data as SessionResponse);
      }
    });
  }
  let controller = sessions.get(sessionId);
  if (!controller) {
    controller = new CodeSessionController(client, sessionId);
    sessions.set(sessionId, controller);
    const data = client.getQueryData<SessionResponse>(codeSessionKeys.detail(sessionId));
    if (data) {
      controller.seed(data, false);
    }
  }
  return controller;
}
